import { Checkbox, Input, InputNumber, message, Modal, Select } from "antd"
import axiosInstance from "../../../../../axios/axiosInstance"
import { useEffect, useState } from "react"
import { ModePage } from "common/enum"
import type { PlanRequest } from "model/request"
import processError from "../../../../../utils/processError"
import { MinusCircleOutlined, PlusOutlined } from "@ant-design/icons"

interface Props {
  isOpen: boolean
  onCloseModal: () => void
  mode: ModePage
  onBeforeSuccess: () => void
  planId: string
}

interface Feature {
  id: string
  name: string
  description?: string
}

export type ProductType = "TICKET" | "BOOK" | "PLAN"

export default function PlanFormModal({ isOpen, onCloseModal, mode, onBeforeSuccess, planId }: Props) {
  const [newPlan, setNewPlan] = useState<PlanRequest | null>(null)
  const [features, setFeatures] = useState<Feature[]>([])
  const [loadingFeatures, setLoadingFeatures] = useState(false)

  const productTypeOptions = [
    { value: "TICKET", label: "Vé" },
    { value: "BOOK", label: "Sách" },
    { value: "PLAN", label: "Gói" },
  ]

  const onInputField = (e: any, fieldName: string) => {
    const newPlanTemp = { ...newPlan } as any
    newPlanTemp[fieldName] = e.target.value
    setNewPlan(newPlanTemp)
  }

  const onNumberField = (value: number | null, fieldName: string) => {
    const newPlanTemp = { ...newPlan } as any
    newPlanTemp[fieldName] = value || 0
    setNewPlan(newPlanTemp)
  }

  const onCheckboxField = (e: any, fieldName: string) => {
    const newPlanTemp = { ...newPlan } as any
    newPlanTemp[fieldName] = e.target.checked
    setNewPlan(newPlanTemp)
  }

  const onSelectField = (value: any, fieldName: string) => {
    const newPlanTemp = { ...newPlan } as any
    newPlanTemp[fieldName] = value
    setNewPlan(newPlanTemp)
  }

  const addFeature = () => {
    if (!newPlan) return
    const newFeatures = [...(newPlan.features || [])]
    newFeatures.push({
      feature: { id: "" },
      orderNumber: newFeatures.length + 1,
    })
    setNewPlan({
      ...newPlan,
      features: newFeatures,
    })
  }

  const removeFeature = (index: number) => {
    if (!newPlan || !newPlan.features) return
    const newFeatures = [...newPlan.features]
    newFeatures.splice(index, 1)
    // Update order numbers
    newFeatures.forEach((feature, idx) => {
      feature.orderNumber = idx + 1
    })
    setNewPlan({
      ...newPlan,
      features: newFeatures,
    })
  }

  const updateFeature = (index: number, featureId: string) => {
    if (!newPlan || !newPlan.features) return
    const newFeatures = [...newPlan.features]
    newFeatures[index].feature = { id: featureId }
    setNewPlan({
      ...newPlan,
      features: newFeatures,
    })
  }

  const updateFeatureOrder = (index: number, order: number) => {
    if (!newPlan || !newPlan.features) return
    const newFeatures = [...newPlan.features]
    newFeatures[index].orderNumber = order
    setNewPlan({
      ...newPlan,
      features: newFeatures,
    })
  }

  const loadFeatures = async () => {
    setLoadingFeatures(true)
    try {
      const res = await axiosInstance({
        url: "features",
        method: "get",
      })
      if (res.status === 200) {
        setFeatures(res.data)
      }
    } catch (err) {
      processError(err)
    }
    setLoadingFeatures(false)
  }

  const loadNewPlan = async () => {
    try {
      const newPlan: PlanRequest = {
        name: "",
        price: 0,
        pointFree: 0,
        pointPremium: 0,
        iconUrl: "",
        isContact: false,
        pointFreeEnable: true,
        pointPremiumEnable: true,
        productType: "PLAN",
        features: [],
        orderNumber: 1,
        isPopular: false,
      }
      setNewPlan(newPlan)
    } catch (err) {
      processError(err)
    }
  }

  const loadEditPlan = async () => {
    try {
      const res = await axiosInstance({
        url: `/plan/${planId}`,
        method: "get",
      })

      if (res.status === 200) {
        // Convert the response to a request format
        const planData = res.data
        const planRequest: PlanRequest = {
          name: planData.name,
          price: planData.price,
          pointFree: planData.pointFree,
          pointPremium: planData.pointPremium,
          iconUrl: planData.iconUrl,
          isContact: planData.isContact,
          pointFreeEnable: planData.pointFreeEnable,
          pointPremiumEnable: planData.pointPremiumEnable,
          productType: planData.productType,
          features:
            planData.features?.map((feature: any) => ({
              feature: { id: feature.feature?.id },
              orderNumber: feature.orderNumber,
            })) || [],
          orderNumber: planData.orderNumber,
          isPopular: planData.isPopular,
        }
        setNewPlan(planRequest)
      } else {
        message.error("Có lỗi xảy ra.")
      }
    } catch (err) {
      processError(err)
    }
  }

  const onOk = async () => {
    if (!newPlan?.name) {
      message.error("Vui lòng nhập tên gói")
      return
    }
    try {
      if (newPlan) {
        await axiosInstance({
          url: mode === ModePage.ADD ? "/plan" : `/plan/${planId}`,
          method: mode === ModePage.ADD ? "post" : "put",
          data: newPlan,
        })
        onBeforeSuccess()
        message.success(mode === ModePage.ADD ? `Thêm thành công` : `Sửa thành công`)
      }
    } catch (err) {
      processError(err)
    }
  }

  useEffect(() => {
    if (isOpen) {
      loadFeatures()
      mode === ModePage.ADD ? loadNewPlan() : loadEditPlan()
    }
  }, [isOpen])

  return (
    <Modal
      open={isOpen}
      onOk={onOk}
      onCancel={onCloseModal}
      title={mode === ModePage.ADD ? "Thêm gói mới" : "Chỉnh sửa gói"}
      width={800}
    >
      <div className="mt-8">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block mb-2">
              Tên gói: <span className="text-red-500">*</span>
            </label>
            <Input
              maxLength={255}
              name="name"
              onChange={(e) => onInputField(e, "name")}
              value={newPlan?.name}
              placeholder="Nhập tên gói..."
            />
          </div>

          <div>
            <label className="block mb-2">Giá:</label>
            <InputNumber
              style={{ width: "100%" }}
              formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
              parser={(value) => Number(value!.replace(/\$\s?|(,*)/g, ""))}
              value={newPlan?.price}
              onChange={(value) => onNumberField(value, "price")}
              min={0}
              placeholder="Nhập giá..."
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 mt-4">
          <div>
            <label className="block mb-2">Điểm miễn phí:</label>
            <InputNumber
              style={{ width: "100%" }}
              value={newPlan?.pointFree}
              onChange={(value) => onNumberField(value, "pointFree")}
              min={0}
              placeholder="Nhập điểm miễn phí..."
            />
          </div>

          <div>
            <label className="block mb-2">Điểm premium:</label>
            <InputNumber
              style={{ width: "100%" }}
              value={newPlan?.pointPremium}
              onChange={(value) => onNumberField(value, "pointPremium")}
              min={0}
              placeholder="Nhập điểm premium..."
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 mt-4">
          <div>
            <label className="block mb-2">URL Icon:</label>
            <Input
              maxLength={255}
              name="iconUrl"
              onChange={(e) => onInputField(e, "iconUrl")}
              value={newPlan?.iconUrl}
              placeholder="Nhập URL icon..."
            />
          </div>

          <div>
            <label className="block mb-2">Loại sản phẩm:</label>
            <Select
              style={{ width: "100%" }}
              value={newPlan?.productType}
              onChange={(value) => onSelectField(value, "productType")}
              options={productTypeOptions}
              placeholder="Chọn loại sản phẩm..."
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 mt-4">
          <div>
            <label className="block mb-2">Thứ tự hiển thị:</label>
            <InputNumber
              style={{ width: "100%" }}
              value={newPlan?.orderNumber}
              onChange={(value) => onNumberField(value, "orderNumber")}
              min={1}
              placeholder="Nhập thứ tự hiển thị..."
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 mt-4">
          <div className="space-y-2">
            <div>
              <Checkbox checked={!!newPlan?.pointFreeEnable} onChange={(e) => onCheckboxField(e, "pointFreeEnable")}>
                Kích hoạt điểm miễn phí
              </Checkbox>
            </div>
            <div>
              <Checkbox
                checked={!!newPlan?.pointPremiumEnable}
                onChange={(e) => onCheckboxField(e, "pointPremiumEnable")}
              >
                Kích hoạt điểm premium
              </Checkbox>
            </div>
          </div>

          <div className="space-y-2">
            <div>
              <Checkbox checked={!!newPlan?.isPopular} onChange={(e) => onCheckboxField(e, "isPopular")}>
                Đánh dấu là phổ biến
              </Checkbox>
            </div>
            <div>
              <Checkbox checked={!!newPlan?.isContact} onChange={(e) => onCheckboxField(e, "isContact")}>
                Yêu cầu liên hệ
              </Checkbox>
            </div>
          </div>
        </div>

        <div className="mt-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium">Tính năng của gói</h3>
            <button type="button" onClick={addFeature} className="flex items-center text-blue-500 hover:text-blue-700">
              <PlusOutlined className="mr-1" /> Thêm tính năng
            </button>
          </div>

          {newPlan?.features?.map((feature, index) => (
            <div key={index} className="flex items-center gap-4 mb-3">
              <div className="flex-grow">
                <Select
                  style={{ width: "100%" }}
                  value={feature.feature.id}
                  onChange={(value) => updateFeature(index, value)}
                  placeholder="Chọn tính năng..."
                  loading={loadingFeatures}
                  options={features.map((f) => ({
                    value: f.id,
                    label: f.name,
                    title: f.description,
                  }))}
                  showSearch
                  filterOption={(input, option) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase())}
                />
              </div>
              <div style={{ width: 100 }}>
                <InputNumber
                  style={{ width: "100%" }}
                  value={feature.orderNumber}
                  onChange={(value) => updateFeatureOrder(index, value || 1)}
                  min={1}
                  placeholder="Thứ tự"
                />
              </div>
              <button type="button" onClick={() => removeFeature(index)} className="text-red-500 hover:text-red-700">
                <MinusCircleOutlined />
              </button>
            </div>
          ))}
        </div>
      </div>
    </Modal>
  )
}
